import { useCallback, useEffect, useRef, useState } from "react";
import { api, apiErrorMessage } from "../../api/client";
import { theme, card, emptyFrame, btnPrimary, btnSecondary, inputStyle, countTag } from "../../theme";
import { DimensionHeading, SheetCard, StatusTag, type StatusTone } from "../../components/Drawing";
import { displayRef } from "../../components/controlStatus";
import { frameworkLabel } from "../../nav/frameworkMeta";
import { LoadingBlock, ErrorBlock } from "../../components/Spinner";
import { fmtDate } from "../../utils/format";
import { fmtDateTime } from "../../lib/format";
import { fmtDateOnly } from "../../lib/dateOnly";
import { SAMPLE_RESULT_LABELS, type AuditSample, type AuditSampleItem, type SampleItemResult, type SampleListResponse } from "./auditTools";

// Audit sampling (backend lib/auditSampling.ts): a random sample drawn from a
// register population of this client — leavers, incidents, risks, corrective
// actions, supplier assessments, training records — from a seed the platform
// keeps with the population count and digest, so anyone can draw it again and
// get the same records. The auditor chooses the size (the bureau's methodology,
// not the platform's), records each item as tested or an exception with a note,
// and can raise a finding from an exception.

const RESULT_TONE: Record<SampleItemResult, StatusTone> = { pending: "none", tested: "met", exception: "gap" };

// The API's reason in words (a zod refusal reads "Seed: 8 to 64 letters, digits, - or _"), else the fallback.
const apiErrorText = apiErrorMessage;

interface ControlOption { id: string; frameworkId: string; name: string }

export function AuditSamplingTab({ orgId, frameworkIds, controls, readOnly = false }: { orgId: string; frameworkIds: string[]; controls: ControlOption[]; readOnly?: boolean }) {
  const [list, setList] = useState<SampleListResponse | null>(null);
  const [error, setError] = useState("");
  const [openId, setOpenId] = useState<string | null>(null);

  const load = useCallback(() => {
    setError("");
    api.get<SampleListResponse>(`/auditor/clients/${orgId}/samples`)
      .then(setList)
      .catch(() => setError("Could not load the samples."));
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  if (error) return <ErrorBlock message={error} onRetry={load} />;
  if (!list) return <LoadingBlock />;

  const populationLabel = (key: string) => list.populations[key]?.label ?? key;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {!readOnly && <DrawSampleForm
        orgId={orgId}
        frameworkIds={frameworkIds}
        populations={list.populations}
        maxSize={list.maxSampleSize}
        onDrawn={(s) => { setList((prev) => prev ? { ...prev, samples: [s, ...prev.samples] } : prev); setOpenId(s.id); }}
      />}

      <div>
        <DimensionHeading count={list.samples.length}>Samples</DimensionHeading>
        {list.samples.length === 0 ? (
          <div style={{ ...emptyFrame, fontSize: 13, color: theme.color.faint }}>No sample drawn for this client yet.</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {list.samples.map((s) => (
              <div key={s.id} style={{ ...card, padding: 0 }}>
                <button
                  onClick={() => setOpenId(openId === s.id ? null : s.id)}
                  aria-expanded={openId === s.id}
                  style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: "none", border: "none", cursor: "pointer", textAlign: "left", flexWrap: "wrap" }}
                >
                  <span style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading }}>{populationLabel(s.population)}</span>
                  <span style={{ fontSize: 12, color: theme.color.subtle }}>{s.sampleSize} of {s.populationCount} · {frameworkLabel(s.frameworkId)}</span>
                  <span style={{ fontSize: 11, color: theme.color.faint }}>{fmtDateTime(s.createdAt)}{s.createdByName ? ` · ${s.createdByName}` : ""}</span>
                  <span style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                    {s.summary.exceptions > 0 && <StatusTag tone="gap">{s.summary.exceptions} exception{s.summary.exceptions === 1 ? "" : "s"}</StatusTag>}
                    <StatusTag tone={s.summary.complete ? "met" : s.summary.pending < s.summary.size ? "partial" : "none"}>
                      {s.summary.complete ? "Tested" : `${s.summary.size - s.summary.pending} of ${s.summary.size} tested`}
                    </StatusTag>
                  </span>
                </button>
                {openId === s.id && (
                  <SampleDetail
                    readOnly={readOnly}
                    orgId={orgId}
                    sampleId={s.id}
                    controls={controls.filter((c) => c.frameworkId === s.frameworkId)}
                    onSummary={(summary) => setList((prev) => prev ? { ...prev, samples: prev.samples.map((x) => x.id === s.id ? { ...x, summary } : x) } : prev)}
                  />
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function DrawSampleForm({ orgId, frameworkIds, populations, maxSize, onDrawn }: {
  orgId: string;
  frameworkIds: string[];
  populations: SampleListResponse["populations"];
  maxSize: number;
  onDrawn: (s: AuditSample) => void;
}) {
  const keys = Object.keys(populations);
  const [frameworkId, setFrameworkId] = useState(frameworkIds[0] ?? "");
  const [population, setPopulation] = useState(keys[0] ?? "");
  const [size, setSize] = useState("10");
  const [periodStart, setPeriodStart] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");
  const [purpose, setPurpose] = useState("");
  const [seed, setSeed] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { if (!frameworkIds.includes(frameworkId)) setFrameworkId(frameworkIds[0] ?? ""); }, [frameworkIds, frameworkId]);

  async function draw() {
    setBusy(true);
    setError("");
    try {
      const s = await api.post<AuditSample>(`/auditor/clients/${orgId}/samples`, {
        frameworkId,
        population,
        size: Number(size),
        // Date-only bounds (UTC); the API takes the end to the end of its day.
        periodStart: periodStart || undefined,
        periodEnd: periodEnd || undefined,
        purpose: purpose.trim() || undefined,
        seed: seed.trim() || undefined,
      });
      setPurpose("");
      setSeed("");
      onDrawn(s);
    } catch (err) {
      setError(apiErrorText(err, "Could not draw the sample."));
    } finally {
      setBusy(false);
    }
  }

  const sizeOk = Number(size) >= 1 && Number(size) <= maxSize && Number.isInteger(Number(size));
  const label: React.CSSProperties = { display: "flex", flexDirection: "column", gap: 4, fontSize: 12, color: theme.color.muted, fontWeight: 600 };

  return (
    <SheetCard label="Draw a sample">
      <p style={{ fontSize: 12, color: theme.color.subtle, margin: "0 0 12px", lineHeight: 1.6 }}>
        The platform draws the records at random from a seed it keeps, with the size of the population and a digest of it,
        so the same sample can be drawn again and checked. You choose the sample size.
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 10 }}>
        <label style={label}>
          Standard
          <select value={frameworkId} onChange={(e) => setFrameworkId(e.target.value)} style={inputStyle}>
            {frameworkIds.map((id) => <option key={id} value={id}>{frameworkLabel(id)}</option>)}
          </select>
        </label>
        <label style={label}>
          Population
          <select value={population} onChange={(e) => setPopulation(e.target.value)} style={inputStyle}>
            {keys.map((k) => <option key={k} value={k}>{populations[k].label}</option>)}
          </select>
        </label>
        <label style={label}>
          Sample size
          <input type="number" min={1} max={maxSize} value={size} onChange={(e) => setSize(e.target.value)} style={inputStyle} />
        </label>
        <label style={label}>
          Period from
          <input type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} style={inputStyle} />
        </label>
        <label style={label}>
          Period to
          <input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} style={inputStyle} />
        </label>
      </div>
      {population && <div style={{ fontSize: 11, color: theme.color.faint, marginTop: 6 }}>Dated by {populations[population]?.datedBy}.</div>}
      <label style={{ ...label, marginTop: 10 }}>
        Purpose (optional)
        <input value={purpose} maxLength={500} onChange={(e) => setPurpose(e.target.value)} placeholder="For example: access removed within one working day of leaving" style={inputStyle} />
      </label>
      <details style={{ marginTop: 10 }}>
        <summary style={{ fontSize: 12, color: theme.color.accentText, cursor: "pointer" }}>Draw again from a known seed</summary>
        <label style={{ ...label, marginTop: 8 }}>
          Seed
          <input value={seed} maxLength={64} onChange={(e) => setSeed(e.target.value)} placeholder="Leave empty for a new random seed" style={inputStyle} />
        </label>
      </details>
      {error && <div role="alert" style={{ fontSize: 12, color: theme.color.dangerText, marginTop: 8 }}>{error}</div>}
      <button onClick={draw} disabled={busy || !frameworkId || !population || !sizeOk} style={{ ...btnPrimary, marginTop: 12, opacity: busy || !sizeOk ? 0.6 : 1 }}>
        {busy ? "Drawing" : "Draw sample"}
      </button>
    </SheetCard>
  );
}

interface SampleDetailResponse extends AuditSample { items: AuditSampleItem[] }
interface VerifyResponse { populationUnchanged: boolean; populationCountNow: number; redrawMatches: boolean }

function SampleDetail({ orgId, sampleId, controls, onSummary, readOnly = false }: {
  readOnly?: boolean;
  orgId: string;
  sampleId: string;
  controls: ControlOption[];
  onSummary: (summary: AuditSample["summary"]) => void;
}) {
  const [sample, setSample] = useState<SampleDetailResponse | null>(null);
  const [error, setError] = useState("");
  const [verify, setVerify] = useState<VerifyResponse | null>(null);
  const [verifying, setVerifying] = useState(false);
  // The parent passes a fresh callback each render; reading it through a ref keeps the load from re-running.
  const onSummaryRef = useRef(onSummary);
  onSummaryRef.current = onSummary;

  const load = useCallback(() => {
    api.get<SampleDetailResponse>(`/auditor/clients/${orgId}/samples/${sampleId}`)
      .then((s) => { setSample(s); onSummaryRef.current(s.summary); })
      .catch(() => setError("Could not open the sample."));
  }, [orgId, sampleId]);

  useEffect(() => { load(); }, [load]);

  async function runVerify() {
    setVerifying(true);
    try {
      setVerify(await api.get<VerifyResponse>(`/auditor/clients/${orgId}/samples/${sampleId}/verify`));
    } catch {
      setError("Could not re-derive the sample.");
    } finally {
      setVerifying(false);
    }
  }

  function updateItem(next: AuditSampleItem) {
    if (!sample) return;
    const items = sample.items.map((i) => (i.id === next.id ? next : i));
    const pending = items.filter((i) => i.result === "pending").length;
    const summary = {
      size: items.length, pending, tested: items.filter((i) => i.result === "tested").length,
      exceptions: items.filter((i) => i.result === "exception").length, complete: items.length > 0 && pending === 0,
    };
    setSample({ ...sample, items, summary });
    onSummaryRef.current(summary);
  }

  if (error) return <div role="alert" style={{ padding: "0 14px 12px", fontSize: 12, color: theme.color.dangerText }}>{error}</div>;
  if (!sample) return <div style={{ padding: 14 }}><LoadingBlock /></div>;

  return (
    <div style={{ borderTop: `1px solid ${theme.color.line}`, padding: "12px 14px", display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", fontSize: 11, color: theme.color.subtle, alignItems: "center" }}>
        <span>Seed <code style={{ fontFamily: "var(--font-mono, monospace)" }}>{sample.seed}</code></span>
        <span>Population {sample.populationCount}</span>
        <span>Period {sample.periodStart ? fmtDateOnly(sample.periodStart) : "any"} to {sample.periodEnd ? fmtDateOnly(sample.periodEnd) : "any"}</span>
        {sample.purpose && <span>{sample.purpose}</span>}
        <button onClick={runVerify} disabled={verifying} style={{ ...btnSecondary, fontSize: 11, padding: "2px 9px", marginLeft: "auto" }}>
          {verifying ? "Checking" : "Re-derive from today's register"}
        </button>
      </div>
      {verify && (
        <div role="status" style={{ fontSize: 12, color: theme.color.text }}>
          {verify.populationUnchanged && verify.redrawMatches
            ? <StatusTag tone="met">Same population, same sample</StatusTag>
            : <StatusTag tone="partial">The register has changed since the draw ({verify.populationCountNow} records now); the sample stands as drawn</StatusTag>}
        </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {sample.items.map((item) => (
          <SampleItemRow
            readOnly={readOnly}
            key={item.id}
            orgId={orgId}
            sampleId={sample.id}
            item={item}
            controls={controls}
            onChange={updateItem}
          />
        ))}
      </div>
    </div>
  );
}

function SampleItemRow({ orgId, sampleId, item, controls, onChange, readOnly = false }: {
  readOnly?: boolean;
  orgId: string;
  sampleId: string;
  item: AuditSampleItem;
  controls: ControlOption[];
  onChange: (item: AuditSampleItem) => void;
}) {
  const [result, setResult] = useState<SampleItemResult>(item.result);
  const [note, setNote] = useState(item.note ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [raising, setRaising] = useState(false);
  const [controlId, setControlId] = useState(controls[0]?.id ?? "");
  const [findingText, setFindingText] = useState("");

  const dirty = result !== item.result || (note.trim() || null) !== (item.note ?? null);

  async function save() {
    setSaving(true);
    setError("");
    try {
      const updated = await api.put<AuditSampleItem>(`/auditor/clients/${orgId}/samples/${sampleId}/items/${item.id}`, { result, note: note.trim() || null });
      onChange(updated);
    } catch (err) {
      setError(apiErrorText(err, "Could not record the result."));
    } finally {
      setSaving(false);
    }
  }

  async function raiseFinding() {
    setSaving(true);
    setError("");
    try {
      const res = await api.post<{ finding: { id: string } }>(`/auditor/clients/${orgId}/samples/${sampleId}/items/${item.id}/finding`, {
        controlId, text: findingText.trim() || note.trim(),
      });
      setRaising(false);
      onChange({ ...item, findingId: res.finding.id });
    } catch (err) {
      setError(apiErrorText(err, "Could not raise the finding."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ border: `1px solid ${theme.color.border}`, borderRadius: 3, padding: "8px 10px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span style={{ ...countTag }}>{item.position}</span>
        <span style={{ fontSize: 12, color: theme.color.heading, fontWeight: 600, flex: 1, minWidth: 180 }}>{item.label}</span>
        {item.recordDate && <span style={{ fontSize: 11, color: theme.color.faint }}>{fmtDate(item.recordDate)}</span>}
        <StatusTag tone={RESULT_TONE[item.result]}>{SAMPLE_RESULT_LABELS[item.result]}</StatusTag>
        {item.findingId && <StatusTag tone="gap">Finding raised</StatusTag>}
      </div>
      {readOnly ? (
        item.note ? <div style={{ fontSize: 12, color: theme.color.text, marginTop: 6 }}>{item.note}</div> : null
      ) : <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap", alignItems: "flex-start" }}>
        <select
          aria-label={`Result for item ${item.position}`}
          value={result}
          disabled={!!item.findingId}
          onChange={(e) => setResult(e.target.value as SampleItemResult)}
          style={{ ...inputStyle, width: "auto", fontSize: 12, padding: "4px 8px" }}
        >
          {(Object.keys(SAMPLE_RESULT_LABELS) as SampleItemResult[]).map((r) => <option key={r} value={r}>{SAMPLE_RESULT_LABELS[r]}</option>)}
        </select>
        <textarea
          aria-label={`Note for item ${item.position}`}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={1}
          maxLength={2000}
          placeholder={result === "exception" ? "What was wrong (required)" : "Note (optional)"}
          style={{ ...inputStyle, flex: 1, minWidth: 200, fontSize: 12, padding: "4px 8px", resize: "vertical" }}
        />
        <button onClick={save} disabled={saving || !dirty} style={{ ...btnPrimary, fontSize: 11, padding: "5px 12px", opacity: saving || !dirty ? 0.6 : 1 }}>
          Save
        </button>
        {item.result === "exception" && !item.findingId && controls.length > 0 && (
          <button onClick={() => setRaising((r) => !r)} style={{ ...btnSecondary, fontSize: 11, padding: "5px 12px" }}>
            {raising ? "Cancel" : "Raise finding"}
          </button>
        )}
      </div>}
      {raising && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }}>
          <select aria-label="Control the finding is against" value={controlId} onChange={(e) => setControlId(e.target.value)} style={{ ...inputStyle, fontSize: 12 }}>
            {controls.map((c) => <option key={c.id} value={c.id}>{displayRef(c.id)} {c.name}</option>)}
          </select>
          <textarea
            aria-label="Finding"
            value={findingText}
            onChange={(e) => setFindingText(e.target.value)}
            rows={2}
            placeholder={note || "Describe the finding"}
            style={{ ...inputStyle, fontSize: 12 }}
          />
          <button onClick={raiseFinding} disabled={saving || !controlId || !(findingText.trim() || note.trim())} style={{ ...btnPrimary, fontSize: 11, padding: "5px 12px", alignSelf: "flex-start" }}>
            Raise open finding
          </button>
        </div>
      )}
      {error && <div role="alert" style={{ fontSize: 11, color: theme.color.dangerText, marginTop: 4 }}>{error}</div>}
    </div>
  );
}
